// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { NOTE_LIST_SCOPES } from '../dto/portal-attendance.js';
import { ATTENDANCE_NOTE_CATEGORIES, ATTENDANCE_NOTE_STATUSES, REGULARISATION_STATUSES, REGULARISATION_TYPES, } from '../enums.js';
import { isoDateSchema, paginationQuerySchema, uuidSchema } from '../common.js';
const RANGE_MAX_DAYS = 366;
const withinDays = (from, to, max) => !from || !to || Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`) <= (max - 1) * 86_400_000;
// ----- regularisation register ---------------------------------------------------------------------------------------------------
const regularisationFilters = {
    status: z.enum(REGULARISATION_STATUSES).optional(),
    type: z.enum(REGULARISATION_TYPES).optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    employeeId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
};
const orderedRange = (v) => !v.from || !v.to || v.to >= v.from;
/** GET /orgs/:orgId/attendance/regularisations — the organisation's regularisation requests (branch scope applies). */
export const regularisationAdminQuerySchema = paginationQuerySchema.extend(regularisationFilters)
    .refine(orderedRange, { message: 'to must be on/after from', path: ['to'] });
/** GET …/attendance/regularisations/export — the same filters, every matching row (bounded). */
export const REGULARISATION_EXPORT_MAX_ROWS = 10_000;
export const regularisationExportQuerySchema = z.object(regularisationFilters)
    .refine(orderedRange, { message: 'to must be on/after from', path: ['to'] });
export const REGULARISATION_ADMIN_DECISIONS = ['approve', 'reject'];
const needsComment = (v) => v.decision !== 'reject' || !!v.comment;
/** POST …/attendance/regularisations/:id/decide — decides the current level of the linked request. */
export const regularisationDecideSchema = z.object({
    decision: z.enum(REGULARISATION_ADMIN_DECISIONS),
    comment: z.string().trim().max(1000).optional(),
    /** The level the caller is looking at (an override must name it; absent = the request's current level). */
    stepNo: z.number().int().min(1).max(20).optional(),
    /** The waiting seat an override fills — required by the engine on an ALL / QUORUM level with several seats waiting (§9.8). */
    onBehalfOfUserId: uuidSchema.optional(),
}).refine(needsComment, { message: 'A comment is required when rejecting.', path: ['comment'] });
export const REGULARISATION_BULK_MAX = 100;
/** POST …/attendance/regularisations/bulk-decide — each item decided on its own (per-item authorisation and result). */
export const regularisationBulkDecideSchema = z.object({
    /** Each item names the level it was on and, for an override on a level waiting for several approvers, the seat it fills. */
    items: z.array(z.object({ id: uuidSchema, stepNo: z.number().int().min(1).max(20).optional(), onBehalfOfUserId: uuidSchema.optional() })).min(1).max(REGULARISATION_BULK_MAX),
    decision: z.enum(REGULARISATION_ADMIN_DECISIONS),
    comment: z.string().trim().max(1000).optional(),
}).refine(needsComment, { message: 'A comment is required when rejecting.', path: ['comment'] })
    .refine((v) => new Set(v.items.map((i) => i.id)).size === v.items.length, { message: 'Each request may appear once.', path: ['items'] });
// ----- comments & approvals report --------------------------------------------------------------------------------------------------
const notesReportFilters = {
    /** mine = my direct reports and reasons routed to me; team = my direct reports; all = organisation-wide oversight. */
    scope: z.enum(NOTE_LIST_SCOPES).default('all'),
    from: isoDateSchema,
    to: isoDateSchema,
    status: z.enum(ATTENDANCE_NOTE_STATUSES).optional(),
    category: z.enum(ATTENDANCE_NOTE_CATEGORIES).optional(),
    branchId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    employeeId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
};
const reportRange = (v) => v.to >= v.from && withinDays(v.from, v.to, RANGE_MAX_DAYS);
/** GET /orgs/:orgId/attendance/notes/report — one row per reason in [from, to] (at most 366 days). */
export const notesReportQuerySchema = paginationQuerySchema.extend(notesReportFilters)
    .refine(reportRange, { message: `to must be on/after from, at most ${RANGE_MAX_DAYS} days`, path: ['to'] });
export const NOTES_REPORT_EXPORT_MAX_ROWS = 20_000;
export const notesReportExportQuerySchema = z.object(notesReportFilters)
    .refine(reportRange, { message: `to must be on/after from, at most ${RANGE_MAX_DAYS} days`, path: ['to'] });
/** What the reason cost the employee: paid leave charged, loss of pay, or nothing. */
export const NOTE_IMPACTS = ['none', 'leave', 'lop', 'excused', 'pending'];
//# sourceMappingURL=attendance-admin.js.map